import { useMemo } from 'react';

import { isAnyItemActive } from 'components/shared/side-menu/side-menu.utils';

import type { NavItem } from 'components/shared/side-menu/nav-config';

import BaseCollapsedSectionWithIcon from './base-collapsed-section-with-icon';
import BaseCollapsedSection from './base-collapsed-section';
import BaseExpandedSection from './base-expanded-section';

interface BaseSideMenuSectionProps {
  title?: string;
  items: NavItem[];
  isCollapsed: boolean;
  isExpanded?: boolean;
  onToggle?: () => void;
  showToggle?: boolean;
  sectionIcon?: React.ReactNode;
  titleIcon?: React.ReactNode;
  isItemActive: (item: NavItem) => boolean;
  renderLink: (props: Record<string, unknown>) => React.ReactNode;
  getNotification?: (item: NavItem) => boolean;
  className?: string;
}

const BaseSideMenuSection = ({
  title,
  items,
  isCollapsed,
  isExpanded: sectionExpanded = true,
  onToggle,
  showToggle = false,
  sectionIcon,
  titleIcon,
  isItemActive,
  renderLink,
  getNotification,
  className,
}: BaseSideMenuSectionProps) => {
  const isSectionActive = useMemo(
    () =>
      typeof isItemActive === 'function' &&
      isAnyItemActive(items, null, isItemActive),
    [items, isItemActive]
  );

  if (!items.length) {
    return null;
  }

  if (isCollapsed && sectionIcon) {
    return (
      <BaseCollapsedSectionWithIcon
        title={title}
        sectionIcon={sectionIcon}
        visibleItems={items}
        isSectionActive={isSectionActive}
        isItemActive={isItemActive}
        renderLink={renderLink}
        getNotification={getNotification}
        className={className}
      />
    );
  }

  if (isCollapsed) {
    return (
      <BaseCollapsedSection
        title={title}
        visibleItems={items}
        isCollapsed={isCollapsed}
        isItemActive={isItemActive}
        renderLink={renderLink}
        getNotification={getNotification}
        className={className}
      />
    );
  }

  return (
    <BaseExpandedSection
      title={title}
      titleIcon={titleIcon}
      visibleItems={items}
      isCollapsed={isCollapsed}
      sectionExpanded={sectionExpanded}
      showToggle={showToggle}
      onToggle={onToggle}
      isItemActive={isItemActive}
      renderLink={renderLink}
      getNotification={getNotification}
      className={className}
    />
  );
};

export default BaseSideMenuSection;
